import { ChangeDetectionStrategy, Component, computed, inject, Input } from '@angular/core';
import { CommonModule, DatePipe, NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { ResponsiveService } from '../../../shared/services/responsive.service';
import { formatAmount } from '../../../shared/utils';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { StepUpAuthService } from '../../../shared/services/step-up-auth.service';
import { CardLoaderComponent } from '../../../shared/components/card-loader/card-loader.component';

interface ScheduleItem {
  DueDate: string;
  Balance: string;
  Status: string;
  GoToPay: string;
  OverDue: string;
}

interface PaymentScheduleData {
  Installments: {
    PaymentFrequency: string;
    Installment: ScheduleItem[];
  };
}

@Component({
  selector: 'app-payment-schedule',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    NgClass,
    MatIcon,
    MatIconModule,
    ...PSIC_COMPONENTS,
    CardLoaderComponent,
  ],
  templateUrl: './payment-schedule.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentScheduleComponent {
  @Input() dcBillingFailed: any = false;

  private readonly responsive = inject(ResponsiveService);
  private readonly store = inject(Store);
  private readonly stepUpAuth = inject(StepUpAuthService);

  readonly isMobile = toSignal(this.responsive.isMobile$, { initialValue: false });

  private readonly policyNumber = this.stepUpAuth.getPolicyNumber();

  private readonly data = toSignal(
    this.store.select((state: any) => state.policy?.data),
    { initialValue: null }
  );
  readonly isLoading = toSignal(
    this.store.select((state: any) => state.policy.loading || state.policy.paymentScheduleLoading),
    { initialValue: true }
  );

  readonly paymentScheduleData = computed<PaymentScheduleData | null>(() => {
    const policyData = this.data()?.[this.policyNumber];
    const billingSnapshot = policyData?.billingSnapshot;
    const paymentSchedule = policyData?.paymentSchedule;
    const schedule = billingSnapshot?.installmentSchedule;
    if(!schedule) return null;
    
    // Use installmentPlanDescription from getPaymentSchedule response if available
    const paymentFrequency = paymentSchedule?.policyTermInfo?.installmentPlanDescription ?? '';

    const items: ScheduleItem[] = (schedule ?? []).map((s: any) => ({
      DueDate: (s.items && s.items.length > 0) ? (s.items[0]?.dueDate ?? '') : '',
      Balance: s.totalAmount,
      Status: '', //TODO: Need to remove the hardcode and fix based on service response
      GoToPay: '0', //TODO: Need to remove the hardcode and fix based on service response
      OverDue: '0', //TODO: Need to remove the hardcode and fix based on service response
    }));

    return {
      Installments: {
        PaymentFrequency: paymentFrequency,
        Installment: items,
      },
    };
  });

  formatAmount(amount?: string): string {
    return formatAmount(amount);
  }
}
